import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import iconIA from 'assets/images/icone-ia.svg';
import { SuggestionTypes } from 'store/modules/juridico_facil_store/types';
import { Container } from './styles';

interface Props {
  sugestoes: SuggestionTypes[];
  // eslint-disable-next-line react/require-default-props
  threshold?: number;
}

const findScrollableContainer = (): HTMLElement | null => {
  const nodes = Array.from(document.querySelectorAll<HTMLElement>('body *'));

  const scrollable = nodes.find((el) => {
    const style = window.getComputedStyle(el);
    const { overflowY } = style;
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
    return canScroll;
  });

  return scrollable || (document.scrollingElement as HTMLElement) || document.documentElement;
};

const AiSuggestionFloating: React.FC<Props> = ({ sugestoes, threshold = 400 }) => {
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);

  const quartaSugestao = useMemo(() => sugestoes?.[3], [sugestoes]);

  useEffect(() => {
    if (!quartaSugestao || closed) return;

    const container = findScrollableContainer();
    if (!container) return;

    const getScrollTop = () => {
      if (container === document.documentElement || container === document.body) {
        return window.pageYOffset || document.documentElement.scrollTop || 0;
      }
      return container.scrollTop || 0;
    };

    const check = () => {
      const top = getScrollTop();
      if (top > threshold) setVisible(true);
    };

    check();

    const onScroll = () => check();

    container.addEventListener('scroll', onScroll, { passive: true } as any);
    window.addEventListener('resize', check);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', onScroll as any);
      window.removeEventListener('resize', check);
    };
  }, [quartaSugestao, closed, threshold]);

  if (!quartaSugestao || closed) return null;

  return (
    <Container className={visible ? 'show' : ''}>
      <div className="floating-card">
        <button type="button" className="close-btn" onClick={() => setClosed(true)}>
          <span className="material-icons">close</span>
        </button>

        <div className="floating-header">
          <div className="floating-icon">
            <img
              alt="Icone Consultor Jurídico"
              src={iconIA}
              height="24px"
              aria-hidden="true"
              className="ml-1"
            />
          </div>
          <div className="align-items-center">
            <h3 className="ai-title">Sugestão da IA</h3>
            <p className="ai-subtitle m-0 p-0">
              Conteúdos relacionados ao tema que você está lendo
            </p>
          </div>
        </div>

        <span className="floating-badge p-0 px-2">{quartaSugestao.subtema}</span>
        <div className="floating-date">
          {moment(quartaSugestao.data_sessao).format('DD/MM/YYYY')}
        </div>
        <h5 className="floating-title">{quartaSugestao.acordao}</h5>
        <p className="floating-description">{quartaSugestao.enunciado}</p>

        <Link
          to={`/juridico/show/${quartaSugestao.id}`}
          className="floating-link"
          onClick={() => setClosed(true)}
        >
          Ler conteúdo
          <span className="material-icons">arrow_forward</span>
        </Link>
      </div>
    </Container>
  );
};

export default AiSuggestionFloating;
